import type {Three as THREE} from "#three-texture-streaming/three-exports"
import {ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import type {ThreeTiledTextureInstance} from "#three-texture-streaming/three-tiled-texture-instance"
import {MAX_TILED_TEXTURE_DESC_IN_SHADER} from "#three-texture-streaming/three-viewport-texture-tile-updater-constants"
import {TRACE_TILED_TEXTURES} from "#three-texture-streaming/texture-tile-constants"
import type {ExplicitTextureStreamingMode} from "#three-texture-streaming/types"
import {ThreeViewportTextureTileUpdaterMaterial} from "#three-texture-streaming/three-viewport-texture-tile-updater-material"
import {ThreeViewportTextureTileUpdaterMaterialSet} from "#three-texture-streaming/three-viewport-texture-tile-updater-material-set"
import type {TileUpdaterMaterialInfo} from "#three-texture-streaming/tile-updater-material-info"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"

const TRACE = TRACE_TILED_TEXTURES

function hashString(s: string): number {
    let h = 0
    for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
    return h
}

export class ThreeViewportTextureTileUpdaterMaterialCache {
    dispose(): void {}

    getTileRequestShaderMaterials(
        materialInfo: TileUpdaterMaterialInfo,
        frameIndex: number,
        renderer: THREE.WebGPURenderer,
        streamingMode: ExplicitTextureStreamingMode,
        maxPixelsPerTexel: number,
    ): ThreeViewportTextureTileUpdaterMaterialSet {
        const hash = this.computeHash(materialInfo)
        let materialSet = this._materialSetByHash.get(hash)
        if (!materialSet) {
            const referencedTiledTexturesByUvNode = new Map<THREE.Node, ThreeTiledTextureInstance[]>()
            if (materialInfo.referencedTiledTextureInstances.length === 0) {
                // no tiled textures; just a dummy material to facilitate occlusion
                referencedTiledTexturesByUvNode.set(TSL.uv(), [])
            } else {
                materialInfo.referencedTiledTextureInstances.forEach((tiledTextureInstance) => {
                    const referencedTiledTexturesForUvNode = referencedTiledTexturesByUvNode.get(tiledTextureInstance.uvNode)
                    if (!referencedTiledTexturesForUvNode) {
                        referencedTiledTexturesByUvNode.set(tiledTextureInstance.uvNode, [tiledTextureInstance])
                    } else {
                        referencedTiledTexturesForUvNode.push(tiledTextureInstance)
                    }
                })
            }
            const materials: ThreeViewportTextureTileUpdaterMaterial[] = []
            for (const [uvNode, referencedTiledTexturesForUvNode] of referencedTiledTexturesByUvNode) {
                if (referencedTiledTexturesForUvNode.length > MAX_TILED_TEXTURE_DESC_IN_SHADER) {
                    logger.warn(
                        `ThreeViewportTextureTileUpdater: mesh references more tiled textures (${referencedTiledTexturesForUvNode.length}) than supported in shader (${MAX_TILED_TEXTURE_DESC_IN_SHADER}); some tiled textures will be ignored`,
                    )
                }
                const opacity = materialInfo.isTransparent ? 0.5 : 1.0
                const material = new ThreeViewportTextureTileUpdaterMaterial(
                    renderer,
                    uvNode,
                    referencedTiledTexturesForUvNode,
                    opacity,
                    streamingMode,
                    maxPixelsPerTexel,
                )
                materials.push(material)
            }
            materialSet = new ThreeViewportTextureTileUpdaterMaterialSet(materials, () => {
                if (!this._materialSetByHash.has(hash)) {
                    throw new Error("ThreeViewportTextureTileUpdaterMaterialCache: internal error: material info not found on dispose")
                }
                this._materialSetByHash.delete(hash)
                if (TRACE) {
                    logger.log(
                        `ThreeViewportTextureTileUpdaterMaterialCache: Removed material set for hash ${hash}. Remaining sets: ${this._materialSetByHash.size}`,
                    )
                }
            })
            this._materialSetByHash.set(hash, materialSet)
        } else {
            materialSet = materialSet.addRef()
        }
        materialSet.updateFrameIndex(frameIndex)
        return materialSet
    }

    private computeHash(materialInfo: TileUpdaterMaterialInfo): number {
        const instanceHashes = materialInfo.referencedTiledTextureInstances.map((instance) => {
            const tiledTextureHash = instance.tiledTexture.id * 31
            const uvNodeHash = hashString(instance.uvNode.uuid) * 17
            const minMipLevelHash = instance.minMipLevel * 13
            return tiledTextureHash + uvNodeHash + minMipLevelHash
        })
        let hash = materialInfo.isTransparent ? 1 : 0
        instanceHashes.forEach((instanceHash) => {
            hash = (hash * 397) ^ instanceHash
        })
        return hash
    }

    private _materialSetByHash = new Map<number, ThreeViewportTextureTileUpdaterMaterialSet>()
}
